/**
 * useInlineEditor — thin wrapper around `contenteditable` elements.
 *
 * - Plain-text only (pastes are sanitized to textContent).
 * - Debounces commits so every keystroke doesn't round-trip.
 * - Ignores server-pushed value while the element holds focus (prevents
 *   cursor jumps during collaborative edits — last-writer-wins).
 * - Single-line mode: Enter commits + blurs. Esc reverts to last committed value.
 */

import { useCallback, useEffect, useRef } from 'react'

interface UseInlineEditorOptions {
  value: string
  onCommit: (next: string) => void
  singleLine?: boolean
  debounceMs?: number
}

interface InlineEditorHandlers {
  ref: React.RefObject<HTMLElement | null>
  onInput: React.FormEventHandler<HTMLElement>
  onKeyDown: React.KeyboardEventHandler<HTMLElement>
  onPaste: React.ClipboardEventHandler<HTMLElement>
  onBlur: React.FocusEventHandler<HTMLElement>
}

export function useInlineEditor({
  value,
  onCommit,
  singleLine = false,
  debounceMs = 400,
}: UseInlineEditorOptions): InlineEditorHandlers {
  const ref = useRef<HTMLElement | null>(null)
  const lastCommittedRef = useRef(value)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync incoming value → DOM (only when not focused; otherwise would jump the caret).
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const hasFocus = document.activeElement === el
    if (hasFocus) return
    if (el.textContent !== value) el.textContent = value
    lastCommittedRef.current = value
  }, [value])

  const flushCommit = useCallback(() => {
    const el = ref.current
    if (!el) return
    const next = el.textContent ?? ''
    if (next === lastCommittedRef.current) return
    lastCommittedRef.current = next
    onCommit(next)
  }, [onCommit])

  const scheduleCommit = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(flushCommit, debounceMs)
  }, [flushCommit, debounceMs])

  const onInput: React.FormEventHandler<HTMLElement> = useCallback(() => {
    scheduleCommit()
  }, [scheduleCommit])

  const onKeyDown: React.KeyboardEventHandler<HTMLElement> = useCallback((e) => {
    if (singleLine && e.key === 'Enter') {
      e.preventDefault()
      flushCommit()
      ;(e.currentTarget as HTMLElement).blur()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      const el = ref.current
      if (el) el.textContent = lastCommittedRef.current
      ;(e.currentTarget as HTMLElement).blur()
    }
  }, [singleLine, flushCommit])

  const onPaste: React.ClipboardEventHandler<HTMLElement> = useCallback((e) => {
    // Always paste plain text — no HTML leakage from other apps.
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }, [])

  const onBlur: React.FocusEventHandler<HTMLElement> = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    flushCommit()
  }, [flushCommit])

  // Cleanup on unmount: final flush.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        flushCommit()
      }
    }
  }, [flushCommit])

  return { ref, onInput, onKeyDown, onPaste, onBlur }
}
